import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { updateProjectsStagesStatus, IProjectStage, ISortProject, handleUpdateCurrentActiveStepApi, Stagestep, CurrentStageStep, findPrevStep, findStepIndex, findNextStep } from '../../../modules/project'
import { Field, Form, Formik, FormikProps, getIn, setIn } from 'formik'
import { isEqual } from 'lodash'
import { KTIcon } from '../../../../_theme/helpers'
import NotFound from '../../apps/components/NotFound'
import { useSnackbar } from '../../../../_theme/context/SnackbarContext'
import { ApiResponse, IDocumentData } from '../../core/_models'
import { useNavigate } from 'react-router-dom'
import { getProjectStageLink, stageCompletedCode } from '../../../../utils/config'
import SubmitConfirmationModal from '../../apps/components/SubmitConfirmationModal'
import StepChangeAlertModal from '../../apps/components/StepChangeAlertModal'
import {
    Send,
    CircleDot,
    Globe,
    BarChart2,
    DollarSign,
    FileText,
    Zap,
    BarChart3,
    Users,
    BookOpen,
    Briefcase,
    Layers
} from "lucide-react";
import DynamicSymbol from '../../apps/components/DynamicSymbol'
import ThemeTooltip from '../../apps/components/UI/ThemeTooltip'
import { strategicResourcingStageSchema, IStrategicResourcingStageSchema, stepTabSchemas, findIncompleteStepTab } from '../components/SR-steps/core/_validation'
import StepIconSidebar from './StepIconSidebar'
import { strategicResourcingDefaultValues } from '../components/SR-steps/core/defaultValues'
import { IProjectScopeResourcesNeed, IBaseCaseAndCounterfactualScenarioEngine, IEconomiesOfScaleAssessment, IFiscalSustainabilityEngine, ISpecialProcurementActionsAndSocialImpact, IStrategicResourcingStepTypes, STEP_TABS } from '../components/SR-steps/core/_models'
import { saveProjectStageTwo, saveStepTab, updateProjectScopeResourcesNeed, updateBaseCaseAndCounterfactualScenario, updateEconomiesOfScaleAssessment, updateFiscalSustainability, updateSpecialProcurementActionsAndSocialImpact } from '../components/SR-steps/core/_request'
import { LogError } from '../../../../utils/GlobalLogging'
import { updateProjectDocuments } from '../core/document/_request'
import { toFormFields } from '../core/formFields'


const SubmitStep = lazy(() => import('../components/common/SubmitStep').then(module => ({ default: module.SubmitStep })));
import { getApiErrorMessage } from "../../../../utils/utils";
const EconomiesOfScaleAssessment = lazy(() => import('../components/SR-steps/EconomiesOfScaleAssessment').then(module => ({ default: module.EconomiesOfScaleAssessment })));
const SpecialProcurementActionsAndSocialImpact = lazy(() => import('../components/SR-steps/SpecialProcurementActionsAndSocialImpact').then(module => ({ default: module.SpecialProcurementActionsAndSocialImpact })));
const ProjectScopeResourcesNeed = lazy(() => import('../components/SR-steps/ProjectScopeResourcesNeed').then(module => ({ default: module.ProjectScopeResourcesNeed })));
const BaseCaseAndCounterfactualScenarioEngine = lazy(() => import('../components/SR-steps/BaseCaseAndCounterfactualScenarioEngine').then(module => ({ default: module.BaseCaseAndCounterfactualScenarioEngine })));
const DemandAndCapacityValidation = lazy(() => import('../components/SR-steps/DemandAndCapacityValidation').then(module => ({ default: module.DemandAndCapacityValidation })));
const ProcurementStrategy = lazy(() => import('../components/SR-steps/ProcurementStrategy').then(module => ({ default: module.ProcurementStrategy })));
const GovernancePolicyEnablingEnvironment = lazy(() => import('../components/SR-steps/GovernancePolicyEnablingEnvironment').then(module => ({ default: module.GovernancePolicyEnablingEnvironment })));
const FinancialAndFundingStrategyEngine = lazy(() => import('../components/SR-steps/FinancialAndFundingStrategyEngine').then(module => ({ default: module.FinancialAndFundingStrategyEngine })));
const FiscalSustainabilityEngineIFSA = lazy(() => import('../components/SR-steps/FiscalSustainabilityEngineIFSA').then(module => ({ default: module.FiscalSustainabilityEngineIFSA })));
const StrategicResourcingDecisionEngineCoreOutput = lazy(() => import('../components/SR-steps/StrategicResourcingDecisionEngineCoreOutput').then(module => ({ default: module.StrategicResourcingDecisionEngineCoreOutput })));
const Documents = lazy(() => import('../components/common/DocumentStep').then(module => ({ default: module.DocumentStep })));
type Props = {
    project: ISortProject;
    stage: IProjectStage;
    isApprover: boolean;
    readonlyAccess: boolean;
    canSubmit: boolean;
    user_id: string;
}

const READ_ONLY_STATUSES = ["stage_status_3", "stage_status_4", "stage_status_6"];

const FIRST_STEP_TAB = 1;

type ExtendedStrategicResourcingStepTypes = IStrategicResourcingStepTypes & {
    formAction?: string;
}

const baseStepperData: Stagestep[] = [
    {
        title: "Project Scope and Resource Needs",
        key: "project_scope_resources_need",
        icon: <Layers size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        description: "",
        components: ProjectScopeResourcesNeed,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Base Case & Counterfactual Scenario Engine",
        key: "base_case_and_counterfactual_scenario_engine",
        icon: <Globe size={22} />,
        iconColor: "text-success",
        iconBackground: "bg-light-success",
        description: "",
        components: BaseCaseAndCounterfactualScenarioEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Demand and Capacity Validation",
        key: "demand_and_capacity_validation",
        icon: <BarChart2 size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        description: "",
        components: DemandAndCapacityValidation,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Procurement Strategy",
        key: "procurement_strategy",
        icon: <Briefcase size={22} />,
        iconColor: "text-warning",
        iconBackground: "bg-light-warning",
        description: "",
        components: ProcurementStrategy,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Governance, Policy & Enabling Environment",
        key: "governance_policy_enabling_environment",
        icon: <BookOpen size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        description: "",
        components: GovernancePolicyEnablingEnvironment,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Financial & Funding Strategy Engine",
        key: "financial_and_funding_strategy_engine",
        icon: <DollarSign size={22} />,
        iconColor: "text-orange",
        iconBackground: "bg-light-orange",
        description: "",
        tooltip: "Funding strategy evaluates project affordability, financing readiness, and long-term fiscal sustainability. Aligned with Infrastructure Fiscal Sustainability Assessment (IFISA) and BFI requirements.",
        components: FinancialAndFundingStrategyEngine,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Fiscal Sustainability Engine — IFISA",
        key: "fiscal_sustainability_engine_ifsa",
        icon: <FileText size={22} />,
        iconColor: "text-danger",
        iconBackground: "bg-light-danger",
        description: "",
        tooltip: "Aligned to Infrastructure Fiscal Sustainability Assessment (IFISA) requirements mandated by National Treasury. Evaluates long-term affordability, fiscal capacity, debt exposure, and contingent liability risks.",
        components: FiscalSustainabilityEngineIFSA,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Special (e.g. Gender) Procurement Actions & Social Impact",
        key: "special_procurement_actions_and_social_impact",
        icon: <Users size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        description: "",
        components: SpecialProcurementActionsAndSocialImpact,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Economies of Scale",
        key: "economies_of_scale_engine",
        icon: <BarChart3 size={22} />,
        iconColor: "text-success",
        iconBackground: "bg-light-success",
        description: "",
        components: EconomiesOfScaleAssessment,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Documents",
        key: "documents",
        icon: <FileText size={22} />,
        iconColor: "text-primary",
        iconBackground: "bg-light-primary",
        description: "",
        components: Documents,
        showIndex: false,
        isAllowNextStep: true,
    },
    {
        title: "Strategic Resourcing Decision Engine — Core Output",
        key: "strategic_resourcing_decision_engine_core_output",
        icon: <Zap size={22} />,
        iconColor: "text-info",
        iconBackground: "bg-light-info",
        description: "",
        components: StrategicResourcingDecisionEngineCoreOutput,
        showIndex: false,
        isAllowNextStep: true,
        showContinue: true,
    }
];

const StrategicResourcing: React.FC<Props> = ({ user_id, project, stage, isApprover, readonlyAccess, canSubmit }) => {
    const project_id = project.project_id;

    const stepperData: Stagestep[] = useMemo(() => {
        return [
            ...baseStepperData,
            {
                title: stage.send_to_approval === "yes"
                    ? "Approval"
                    : "Submit",
                key: "approval_submit",
                showIndex: false,
                icon: <Send size={22} />,
                iconColor: "text-primary",
                iconBackground: "bg-primary-light",
                description:
                    stage.send_to_approval === "yes"
                        ? "Submit the project for approval"
                        : "Submit the project",
                components: SubmitStep
            }
        ];
    }, [stage.send_to_approval]);

    const { showSnackbar } = useSnackbar();

    const navigate = useNavigate();
    const [disableFormFields, setDisableFormFields] = useState<boolean>(false);
    const [submitLoader, setSubmitLoader] = useState<boolean>(false);

    const [currentSchema, setCurrentSchema] = useState(strategicResourcingStageSchema[stepperData[0].key as IStrategicResourcingStageSchema]);
    const [showSubmitConfirmationModal, setShowSubmitConfirmationModal] = useState(false);
    const [stepChangeAlertModal, setStepChnageAlertModal] = useState(false)
    const [pendingStep, setPendingStep] = useState<string | null>(null);

    const [initValues, setInitValues] = useState<IStrategicResourcingStepTypes>(strategicResourcingDefaultValues.project_scope_resources_need);
    const formikRef = useRef<FormikProps<ExtendedStrategicResourcingStepTypes>>(null);
    const [validateOnStepChange, setValidateOnStepChange] = useState(false);
    const [currentStep, setCurrentStep] = useState<CurrentStageStep>(() => {
        const stored = stage.active_step ? stage.active_step.toString() : null;
        return findStepIndex(stepperData, stored !== null ? stored : "project_overview", true);
    });

    // Opens the tab the user asked for while the current one had unsaved edits.
    const [pendingTabChange, setPendingTabChange] = useState<{ open: () => void } | null>(null);

    // The open tab of a tab-wise step (a STEP_TABS tab id), kept with the step it was opened on.
    // A newly opened step reads its first tab in the same render, so a tab left over from the
    // previous step never reaches it.
    const [openTab, setOpenTab] = useState<{ step: CurrentStageStep; tabId: number }>(
        () => ({ step: currentStep, tabId: FIRST_STEP_TAB })
    );
    const stepTab = openTab.step === currentStep ? openTab.tabId : FIRST_STEP_TAB;
    const setStepTab = useCallback((tabId: number) => {
        setOpenTab({ step: currentStep, tabId });
    }, [currentStep]);

    // The open step's tabs when it saves one tab at a time.
    const stepTabs = STEP_TABS[currentStep.step.key];
    const openTabFields = useMemo<readonly string[]>(
        () => stepTabs?.find((tab) => tab.tabId === stepTab)?.fields ?? [],
        [stepTabs, stepTab]
    );

    // Tab-wise steps validate only the open tab; a tab with no required fields has no schema.
    const validationSchema = useMemo(
        () => (stepTabs ? stepTabSchemas[currentStep.step.key]?.[stepTab] : currentSchema),
        [stepTabs, currentStep.step.key, stepTab, currentSchema]
    );

    // Steps that load their values may replace them without formAction; the form always starts with
    // one, so the hidden formAction input stays controlled when a save resets it to its initial value.
    const formInitialValues = useMemo(
        () => ({ ...initValues, formAction: (initValues as ExtendedStrategicResourcingStepTypes)?.formAction ?? "" }) as ExtendedStrategicResourcingStepTypes,
        [initValues]
    );

    const [isAllowNextStep, setIsAllowNextStep] = useState(true);
    // Set by a step whose saved data failed to load: saving its empty form would wipe that data.
    const [saveBlocked, setSaveBlocked] = useState(false);

    const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

    useEffect(() => {
        setCurrentSchema(
            strategicResourcingStageSchema[currentStep.step.key as IStrategicResourcingStageSchema]
        );
        setInitValues(
            strategicResourcingDefaultValues[
            currentStep.step.key as IStrategicResourcingStageSchema
            ] as IStrategicResourcingStepTypes
        );
        setIsAllowNextStep(currentStep.step?.isAllowNextStep === false ? false : true);
        setSaveBlocked(false);
    }, [currentStep]);

    useEffect(() => {
        setDisableFormFields(isApprover || readonlyAccess || READ_ONLY_STATUSES.includes(stage.dk_stage_status));
    }, [isApprover, readonlyAccess, stage])

    //  Fixed handleUpdateActiveStep with useCallback
    const handleUpdateActiveStep = useCallback(async (activeStep: string) => {
        const response = await handleUpdateCurrentActiveStepApi(stage.dk_stage, activeStep, Number(project_id));
        if (response.status !== "ok") {
            showSnackbar(response.message, 'danger');
        }
    }, [stage.dk_stage, project_id, showSnackbar]);

    // Save from the "data will be lost" prompt: submits the form, so the step is validated
    // first, and opens the other step only once the save succeeded.
    const handleSaveAndContinue = useCallback(async (): Promise<void> => {
        const formik = formikRef.current;
        const target = pendingStep;
        if (!formik || !target) return;

        await formik.setFieldValue("formAction", "save", false);
        // Typed as void, but resolves to submitStep's result, or undefined when validation fails.
        const saved: unknown = await formik.submitForm();
        setStepChnageAlertModal(false);
        setPendingStep(null);
        if (saved === true) {
            setCurrentStep(findStepIndex(stepperData, target));
        }
    }, [pendingStep, stepperData]);

    //  Fixed confirmStepChange
    const confirmStepChange = useCallback(() => {
        if (pendingStep) {
            setCurrentStep(findStepIndex(stepperData, pendingStep));
            setPendingStep(null);
        }
        setStepChnageAlertModal(false);
    }, [pendingStep, stepperData]);

    // Resolves true when the step was saved.
    const submitStep = useCallback(async (values: ExtendedStrategicResourcingStepTypes): Promise<boolean> => {
        if (isApprover) return false;
        if (saveBlocked) {
            showSnackbar("This step's saved data failed to load. Reload the page before saving.", "warning");
            return false;
        }
        setSubmitLoader(true);
        try {
            const projectId = Number(project_id);
            const handlers: Record<
                string,
                () => Promise<ApiResponse>
            > = {
                project_scope_resources_need: async () => updateProjectScopeResourcesNeed(projectId, values as IProjectScopeResourcesNeed),
                base_case_and_counterfactual_scenario_engine: async () => updateBaseCaseAndCounterfactualScenario(projectId, values as IBaseCaseAndCounterfactualScenarioEngine),

                // Only the Supporting Documents tab, which has no section, saves through the step handler:
                // its documents upload from their popup, so the tab's Save has nothing to send.
                procurement_strategy: async () => ({ status: "ok", message: "Procurement strategy saved successfully", data: null, errorCode: "" }),
                // Tab-wise steps (Demand and Capacity, Governance, Financial & Funding) have a section per tab, so they save through saveStepTab.
                fiscal_sustainability_engine_ifsa: async () => updateFiscalSustainability(projectId, values as IFiscalSustainabilityEngine),
                economies_of_scale_engine: async () => updateEconomiesOfScaleAssessment(projectId, values as IEconomiesOfScaleAssessment),
                special_procurement_actions_and_social_impact: async () => updateSpecialProcurementActionsAndSocialImpact(projectId, values as ISpecialProcurementActionsAndSocialImpact),
                documents: async () => updateProjectDocuments({ documents: (values as IDocumentData).documents, project_id: projectId, stage_id: stage.dk_stage }),
            };

            // A tab of a tab-wise step saves just that tab's fields to its sub-section.
            const openStepTab = stepTabs?.find((tab) => tab.tabId === stepTab);
            const sectionTab = openStepTab?.section ? { ...openStepTab, section: openStepTab.section } : undefined;
            const handler = sectionTab
                ? () => saveStepTab(projectId, sectionTab, values)
                : handlers[currentStep.step.key];

            const response: ApiResponse = handler
                ? await handler()
                : {
                    status: "error",
                    message: "Something went wrong",
                    data: null,
                    errorCode: "",
                };

            if (response.status === "ok") {
                showSnackbar(response.message, "success");

                let activeStep = currentStep.step.key;
                const isSaveNext = values.formAction === "save-next";
                const openNextStep = () => {
                    const nextStep = findNextStep(currentStep, stepperData);
                    activeStep = nextStep.step.key;
                    setCurrentStep(nextStep);
                };

                if (stepTabs) {
                    // Only the saved tab comes back: mark it saved and keep the
                    // other tabs' values, including any unsaved edits.
                    const formik = formikRef.current;
                    let tabValues = values;
                    if (formik) {
                        const savedFields = response.data ? toFormFields(response.data, values) : {};
                        formik.resetForm({
                            values: { ...formik.initialValues, ...savedFields } as ExtendedStrategicResourcingStepTypes,
                        });
                        // formAction is not a saved value; left set, it would keep the form dirty.
                        tabValues = { ...values, ...savedFields, formAction: formik.initialValues.formAction } as ExtendedStrategicResourcingStepTypes;
                        void formik.setValues(tabValues, false);
                    }

                    // Save & Next opens the next tab; on the last tab it leaves the step only once
                    // every tab is complete, otherwise it opens the first incomplete tab with its errors.
                    const nextStepTab = stepTabs[stepTabs.findIndex((tab) => tab.tabId === stepTab) + 1];
                    if (isSaveNext && nextStepTab) {
                        setStepTab(nextStepTab.tabId);
                    } else if (isSaveNext) {
                        const incomplete = await findIncompleteStepTab(currentStep.step.key, tabValues);
                        if (incomplete) {
                            setStepTab(incomplete.tabId);
                            formik?.setErrors(incomplete.errors);
                            void formik?.setTouched(
                                Object.keys(incomplete.errors).reduce((acc, key) => setIn(acc, key, true), {}),
                                false,
                            );
                            showSnackbar(`Complete ${incomplete.title} before continuing.`, "warning");
                        } else {
                            openNextStep();
                        }
                    }
                } else if (isSaveNext) {
                    openNextStep();
                } else {
                    // What was submitted is now saved, updated with what the API sent back. formAction
                    // is not a saved value; left set, it would keep the form dirty.
                    const formik = formikRef.current;
                    if (formik) {
                        const savedFields = response.data ? toFormFields(response.data, values) : {};
                        formik.resetForm({
                            values: { ...values, ...savedFields, formAction: formik.initialValues.formAction } as ExtendedStrategicResourcingStepTypes,
                        });
                    }
                }

                if (stage.active_step !== activeStep) {
                    await handleUpdateActiveStep(activeStep);
                }
                return true;
            } else {
                showSnackbar(response.message, "danger");
                LogError(response.message || "Error submitting step", response, "StrategicResourcing");
            }
        } catch (error) {
            showSnackbar(getApiErrorMessage(error, "Something went wrong"), "danger");
            LogError("Error submitting step", error, "StrategicResourcing");
        } finally {
            setSubmitLoader(false);
        }
        return false;
    }, [isApprover, saveBlocked, project_id, currentStep, stepTabs, stepTab, setStepTab, stepperData, stage.active_step, stage.dk_stage, showSnackbar, handleUpdateActiveStep]);

    // Switching tabs in a tab-wise step asks first when the open tab has unsaved edits,
    // because Save only covers the open tab.
    const changeStepTab = useCallback((tabId: number) => {
        const formik = formikRef.current;
        if (!formik || tabId === stepTab) return;

        const hasUnsavedEdits = !disableFormFields && openTabFields.some(
            (field) => !isEqual(getIn(formik.values, field), getIn(formik.initialValues, field))
        );
        if (hasUnsavedEdits) {
            setPendingTabChange({ open: () => setStepTab(tabId) });
            return;
        }
        setStepTab(tabId);
    }, [stepTab, setStepTab, openTabFields, disableFormFields]);

    // Continue without saving: put the open tab back to its saved values, then switch.
    const discardAndChangeTab = useCallback(() => {
        const formik = formikRef.current;
        if (formik && pendingTabChange) {
            let restored = formik.values;
            openTabFields.forEach((field) => {
                restored = setIn(restored, field, getIn(formik.initialValues, field));
            });
            void formik.setValues(restored, false);
            formik.setErrors({});
            pendingTabChange.open();
        }
        setPendingTabChange(null);
    }, [openTabFields, pendingTabChange]);

    // Save & Continue: save the open tab (with its validation), and switch only if it saved.
    const saveAndChangeTab = useCallback(async () => {
        const formik = formikRef.current;
        const target = pendingTabChange;
        if (!formik || !target) return;

        await formik.setFieldValue("formAction", "save", false);
        // Typed as void, but resolves to submitStep's result, or undefined when validation fails.
        const saved: unknown = await formik.submitForm();
        setPendingTabChange(null);
        if (saved === true) {
            target.open();
        }
    }, [pendingTabChange]);

    //  Fixed handleSubmitStage
    const handleSubmitStage = useCallback(async () => {
        // find first incomplete step
        // TODO: Implement this later
        /*const incompleteIndex = stepInits.findIndex(
            step => !step.iscompleted
        );
        if (incompleteIndex !== -1 && incompleteIndex != stepInits.length - 1) {
            const targetStep = incompleteIndex + 1;
            // move to that step
            setCurrentStep(findStepIndex(stepperData, stepperData[targetStep].key));
            setValidateOnStepChange(true);
            return;
        }*/

        setShowSubmitConfirmationModal(true);
    }, []);

    //  Fixed validation useEffect with cleanup
    useEffect(() => {
        if (!validateOnStepChange) return;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            if (formikRef.current) {
                formikRef.current.validateForm().then(errors => {
                    const touchedFields = Object.keys(errors).reduce(
                        (acc, key) => {
                            return setIn(acc, key, true);
                        },
                        {}
                    );
                    formikRef.current?.setTouched(touchedFields);
                });
            }
        }, 100);

        setValidateOnStepChange(false);

    }, [initValues, validateOnStepChange]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    //  Fixed handleConfirmSubmitStage
    const handleConfirmSubmitStage = useCallback(async () => {
        setSubmitLoader(true);
        const data = {
            project_id: Number(project_id),
            dk_stage: stage.dk_stage,
        }

        let stageSubmitResp = await saveProjectStageTwo(data);
        if (stageSubmitResp.status === "ok") {
            // if (stage.send_to_approval === "no") {
            //     const data = {
            //         user_id: user_id,
            //         project_id: project_id,
            //         dk_stage: stage.dk_stage
            //     }
            //     await getProjectReport(data, stage.dk_stage);
            // }
            showSnackbar(stageSubmitResp.message, 'success');
            navigate(getProjectStageLink(project_id));
        } else {
            showSnackbar(stageSubmitResp.message, 'danger');
        }
        setSubmitLoader(false);
        setShowSubmitConfirmationModal(false); //  Fixed: Close modal after handling
    }, [user_id, project_id, stage.dk_stage, stage.send_to_approval, navigate, showSnackbar]);

    //  Fixed prevStep
    const prevStep = useCallback((dirty: boolean) => {
        const prev = findPrevStep(currentStep, stepperData);
        if (!["stage_status_3", "stage_status_4", "stage_status_6"].includes(stage.dk_stage_status) && dirty) {
            setPendingStep(prev.step.key);
            setStepChnageAlertModal(true);
            return;
        }
        setCurrentStep(prev)
    }, [currentStep, stepperData, stage.dk_stage_status]);

    const handleContinue = useCallback(() => {
        const nextStep = findNextStep(currentStep, stepperData);
        setCurrentStep(nextStep);
        handleUpdateActiveStep(nextStep.step.key);
    }, [currentStep, stepperData, handleUpdateActiveStep]);

    //  Fixed goToStep
    const goToStep = useCallback((key: string, dirty: boolean) => {
        // Re-selecting the open step would reset its form to defaults without refetching its data.
        if (key === currentStep.step.key) {
            return;
        }
        if (!["stage_status_3", "stage_status_4", "stage_status_6"].includes(stage.dk_stage_status) && dirty) {
            setPendingStep(key);
            setStepChnageAlertModal(true);
            return;
        }
        setCurrentStep(findStepIndex(stepperData, key))
    }, [stepperData, stage.dk_stage_status, currentStep.step.key]);

    //  Fixed handleProjectRequestSubmit (THIS WAS MISSING)
    const handleProjectRequestSubmit = useCallback(async (stageStatus: string, remarks: string) => {
        setSubmitLoader(true);
        const data = {
            user_id: user_id,
            project_id: project_id,
            dk_stage: stage.dk_stage,
            project_stage_id: stage.project_stage_id,
            action: stageStatus,
            remarks: remarks
        }

        let stageSubmitResp: ApiResponse = await updateProjectsStagesStatus(data);
        if (stageSubmitResp.status === "ok") {
            showSnackbar(stageSubmitResp.message, 'success');
            if (stageStatus == stageCompletedCode) {
                const data = {
                    user_id: user_id,
                    project_id: project_id,
                    dk_stage: stage.dk_stage
                }
                // await getProjectReport(data, stage.dk_stage);
                navigate(`/dashboard`);
            } else {
                navigate(`/dashboard`);
            }
        } else {
            setSubmitLoader(false);
            showSnackbar(stageSubmitResp.message, 'danger');
        }
    }, [user_id, project_id, stage.dk_stage, stage.project_stage_id, navigate, showSnackbar]);

    //  Fixed loadStepper
    const loadStepper = useCallback(() => {
        const CurrentStepComponent = currentStep.step.components;
        const StepComponent = CurrentStepComponent as React.ComponentType<{
            submitLoader: boolean;
            project_id: string;
            disableFormFields: boolean;
            setInitValues: React.Dispatch<React.SetStateAction<IStrategicResourcingStepTypes>>;
            isApprover: boolean;
            stage: IProjectStage;
            handleProjectRequestSubmit: (stageStatus: string, remarks: string) => void,
            setIsAllowNextStep: React.Dispatch<React.SetStateAction<boolean>>
            setSaveBlocked: (blocked: boolean) => void;
            stepTab: number;
            setStepTab: (tabId: number) => void;
        }>;

        return (
            <Suspense fallback={<div className="p-4 text-center">Loading step...</div>}>
                <StepComponent
                    submitLoader={submitLoader}
                    project_id={project_id}
                    disableFormFields={disableFormFields}
                    setInitValues={setInitValues}
                    isApprover={isApprover}
                    stage={stage}
                    handleProjectRequestSubmit={handleProjectRequestSubmit}
                    setIsAllowNextStep={setIsAllowNextStep}
                    setSaveBlocked={setSaveBlocked}
                    stepTab={stepTab}
                    setStepTab={changeStepTab}
                />
            </Suspense>
        );
    }, [currentStep.step.components, submitLoader, project_id, disableFormFields, isApprover, stage, handleProjectRequestSubmit, stepTab, changeStepTab]);

    //  Fixed handleFormSubmit to prevent double submission
    const handleFormSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>, handleSubmit: any, setFieldValue: any) => {
        e.preventDefault();

        const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement;
        const action = submitter?.value;

        if (action) {
            await setFieldValue("formAction", action);
        }

        // Call handleSubmit directly without calling requestSubmit again
        handleSubmit(e);
    }, []);

    if (!initValues) {
        return <NotFound />;
    }

    return (
        <div
            className='stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid'
            id='project_stepper'
        >
            <Formik<ExtendedStrategicResourcingStepTypes> innerRef={formikRef} validationSchema={validationSchema} enableReinitialize={true} initialValues={formInitialValues} onSubmit={submitStep}>
                {({ handleSubmit, setFieldValue, dirty, values }: FormikProps<ExtendedStrategicResourcingStepTypes>) => {
                    return <>
                        {/* begin::Aside*/}
                        <StepIconSidebar
                            steps={stepperData}
                            currentStep={currentStep}
                            onStepClick={(key) => goToStep(key, dirty)}
                        />
                        {/* begin::Aside*/}
                        <div className='d-flex flex-row-fluid justify-content-center bg-body rounded'>
                            <Form className='p-6 w-100 position-relative' noValidate id='kt_create_account_form' onSubmit={(e) => handleFormSubmit(e, handleSubmit, setFieldValue)}>
                                <div className='card card-custom'>
                                    <div className='card-header align-items-center'>
                                        <div className='d-flex align-items-center'>
                                            <DynamicSymbol
                                                size={40}
                                                bgClass={`${currentStep.step.iconColor} ${currentStep.step.iconBackground}`}
                                                icon={currentStep.step.icon ? currentStep.step.icon : <CircleDot size={22} />}
                                            />
                                            <div>
                                                <div className='d-flex align-items-center'>
                                                    <h3 className='card-title align-items-center mb-0 mt-0'>{currentStep.step.title}</h3>
                                                    {currentStep.step.tooltip && <ThemeTooltip text={currentStep.step.tooltip} />}
                                                </div>
                                                {currentStep.step.description && <div className="text-muted fs-7 mt-0">{currentStep.step.description}</div>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='card-body pt-2 px-6 pb-4'>
                                        {loadStepper()}
                                        {(!disableFormFields || (currentStep.isLast && canSubmit)) &&
                                            <div className='d-flex flex-stack pt-6 pb-0 px-4'>
                                                <div className='mr-2'>{!currentStep.isFirst &&
                                                    <button
                                                        onClick={() => prevStep(dirty)}
                                                        type='button'
                                                        className='btn btn-md btn-light-primary me-3'
                                                    >
                                                        <KTIcon iconName='arrow-left' className='fs-4 me-1' />
                                                        Back
                                                    </button>}
                                                </div>
                                                <div>
                                                    {currentStep.step.showContinue ?
                                                    <> {isAllowNextStep && 
                                                        <button disabled={submitLoader} type='button' className='btn btn-md btn-primary ms-3' onClick={handleContinue}>
                                                            <span className='indicator-label d-flex flex-center'>
                                                                {'Next'}
                                                                <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                            </span>
                                                        </button> }
                                                        </>
                                                        : <>
                                                            {!currentStep.isLast ? <>
                                                                <button disabled={submitLoader || saveBlocked} type='submit' value='save' className='btn btn-md btn-active-primary btn-color-primary border border-primary btn-active-color-white save-only'>
                                                                    <span className='indicator-label d-flex flex-center'>
                                                                        {submitLoader && values.formAction === "save" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving...</span></> : "Save"}
                                                                    </span>
                                                                </button>
                                                                {isAllowNextStep &&
                                                                    <button disabled={submitLoader || saveBlocked} type='submit' value='save-next' className='btn btn-md btn-primary ms-3'>
                                                                        <span className='indicator-label d-flex flex-center'>
                                                                            {submitLoader && values.formAction === "save-next" ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Saving & opening next...</span></> : "Save & Next"}
                                                                            <KTIcon iconName='arrow-right' className='fs-3 ms-2 me-0' />
                                                                        </span>
                                                                    </button>} </> :
                                                                canSubmit && <button disabled={submitLoader} onClick={handleSubmitStage} type='button' className='btn btn-md btn-primary'>
                                                                    <span className='indicator-label d-flex flex-center'>
                                                                        {submitLoader ? <><span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span><span className='ms-2'>Submitting...</span></> : "Submit"}
                                                                    </span>
                                                                </button>
                                                            }
                                                        </>
                                                    }
                                                    <Field type="hidden" name="formAction" />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            </Form>
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={stepChangeAlertModal}
                                onClose={() => setStepChnageAlertModal(false)}
                                onConfirm={() => confirmStepChange()}
                                onSave={() => void handleSaveAndContinue()}
                                message={`Your updated data in this step will be lost if you move to another step without saving. Do you want to continue?`}
                            />
                            <StepChangeAlertModal
                                popupLoader={submitLoader}
                                show={pendingTabChange !== null}
                                onClose={() => setPendingTabChange(null)}
                                onConfirm={discardAndChangeTab}
                                onSave={() => void saveAndChangeTab()}
                                message={`Your changes in this tab are not saved. Save them before opening another tab, or continue and discard them.`}
                            />
                        </div>
                    </>
                }}
            </Formik>
            <SubmitConfirmationModal
                text="After submitting this stage, you will not be able to make any changes. Are you sure you want to submit this stage?"
                loader={submitLoader}
                show={showSubmitConfirmationModal}
                onClose={() => setShowSubmitConfirmationModal(false)}
                onConfirm={handleConfirmSubmitStage}
            />
        </div>
    )
}

export default StrategicResourcing